import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, CheckCircle2, Copy, Download, Gauge, Info, KeyRound, MousePointerClick, PlugZap, Save, ShieldCheck } from '@/components/icons';
import { api } from '@/lib/api';
import { copyText, formatDateTime } from '@/lib/format';
import { RECORDER_MIN_VERSION, RECORDER_PERF_MIN_VERSION, detectRecorder, fetchLatestRecorder, recorderFileName, versionAtLeast, type RecorderInfo } from '@/lib/recorderBridge';
import { Button, Card, CardBody, CardHeader, PageHeader, buttonClass } from '@/components/ui';

/** Download location of the extension, built into the web image (apps/recorder -> /downloads). */
export const RECORDER_DOWNLOAD = '/downloads/1qa-recorder.zip';

function CopyAddress() {
  const address = window.location.origin;
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <code className="rounded-field bg-surface-sunken px-3 py-1.5 font-mono text-sm text-fg ring-1 ring-border ring-inset">{address}</code>
      <Button
        variant="outline"
        size="sm"
        icon={copied ? <CheckCircle2 className="size-3" /> : <Copy className="size-3" />}
        onClick={async () => {
          if (await copyText(address)) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

const steps = [
  {
    icon: Download,
    title: 'Install the extension',
    body: (
      <ol className="list-decimal space-y-1 pl-5">
        <li>Download the recorder and unzip it into a folder you will keep.</li>
        <li>
          Open <code className="rounded-bar bg-surface-raised px-1.5 py-0.5 font-mono text-xs text-fg">chrome://extensions</code> (Chrome) or <code className="rounded-bar bg-surface-raised px-1.5 py-0.5 font-mono text-xs text-fg">edge://extensions</code> (Edge) and turn on <strong>Developer mode</strong>.
        </li>
        <li>
          Click <strong>Load unpacked</strong> and choose the unzipped folder. Pin <strong>1QA Recorder</strong> to your toolbar.
        </li>
      </ol>
    ),
  },
  {
    icon: PlugZap,
    title: 'Connect it to 1QA',
    body: (
      <div className="space-y-2">
        <p>Click the 1QA Recorder icon, paste this address and press Connect. Because you are signed in to 1QA in this browser, no extra sign-in is needed.</p>
        <CopyAddress />
      </div>
    ),
  },
  {
    icon: MousePointerClick,
    title: 'Record',
    body: (
      <p>
        By default the recorder opens a new <strong>incognito window</strong>: every recording starts signed out, with no cookies or saved data. You can also record <strong>this tab</strong> or a normal <strong>new window</strong>. Then use your
        app as a user would - clicks, typing, dropdowns and checkboxes become plain-English steps you can see in the recorder.
      </p>
    ),
  },
  {
    icon: CheckCircle2,
    title: 'Add checks',
    body: (
      <p>
        Select text on the page, right-click and choose <strong>1QA: Check this text is shown</strong>, or type a check in the recorder (for example
        &ldquo;the cart shows 2 items&rdquo;).
      </p>
    ),
  },
  {
    icon: Save,
    title: 'Save to a test project',
    body: (
      <p>
        Press <strong>Stop &amp; review</strong>, adjust or remove steps, name the test and pick the organization and test project. The test opens in the
        builder, ready to run. Tip: in a project, <strong>New test &gt; Record in the browser</strong> opens your app in a new incognito window and picks the project
        for you.
      </p>
    ),
  },
];

/** Shown when "New test > Record in the browser" sent the user here because the recorder is missing or too old. */
function FromNewTestNotice({ projectId, oldVersion }: { projectId: string; oldVersion: string | null }) {
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId) });
  return (
    <div role="status" className="rounded-card bg-primary-subtle p-4 text-sm text-fg ring-1 ring-primary/40 ring-inset">
      <p className="flex items-center gap-2 font-medium">
        <Info className="size-3.5 shrink-0 text-primary" />
        {oldVersion
          ? `Your recorder (version ${oldVersion}) is too old to start from 1QA - version ${RECORDER_MIN_VERSION} or newer is needed.`
          : 'To record a test, install the 1QA Recorder first.'}
      </p>
      <p className="mt-1 text-fg-secondary">
        {oldVersion
          ? 'Download the recorder again, unzip it over the old folder and press the reload icon on 1QA Recorder in your extensions page. '
          : 'Follow the steps below. '}
        Then reload this page, go back to the project and choose <strong>New test &gt; Record in the browser</strong> again.
      </p>
      <Link to={`/projects/${projectId}`} className="mt-2 inline-flex items-center gap-1.5 font-medium text-primary underline underline-offset-2 hover:no-underline">
        <ArrowLeft className="size-3" /> Back to {project.data?.name ?? 'the project'}
      </Link>
    </div>
  );
}

/** Which recorder build is installed in this browser (it announces itself on 1QA pages) and which build is the latest. */
function VersionStatus() {
  const [installed, setInstalled] = useState<RecorderInfo | null | undefined>(undefined);
  const latest = useQuery({ queryKey: ['recorder-latest'], queryFn: fetchLatestRecorder, staleTime: 60_000 });
  useEffect(() => {
    let alive = true;
    void detectRecorder().then((v) => alive && setInstalled(v));
    return () => {
      alive = false;
    };
  }, []);
  if (installed === undefined) return null;
  const build = latest.data;
  const usable = installed && versionAtLeast(installed.version, RECORDER_MIN_VERSION);
  const outdated = installed && build && !versionAtLeast(installed.version, build.version);
  return (
    <div className="space-y-1 text-sm">
      {installed ? (
        <p className={clsx('flex items-center gap-2', usable && !outdated ? 'text-success' : 'text-warning')}>
          {usable && !outdated ? <CheckCircle2 className="size-3.5 shrink-0" /> : <Info className="size-3.5 shrink-0" />}
          Installed in this browser: version {installed.versionName}
          {outdated
            ? ' - a newer build is available. Download it, unzip it over the old folder and press reload on 1QA Recorder in your extensions page.'
            : !usable
              ? ' - too old to start recordings from 1QA; download the new one.'
              : '.'}
        </p>
      ) : (
        <p className="text-fg-muted">The recorder is not installed in this browser yet (just installed it? Reload this page).</p>
      )}
      {build && (
        <p className="text-fg-muted">
          Latest build: version {build.versionName}, built {formatDateTime(build.builtAt)}
          {build.commit ? ` from ${build.commit}` : ''}.
        </p>
      )}
    </div>
  );
}

export function RecorderPage() {
  const [params] = useSearchParams();
  const fromProject = params.get('project');
  const latest = useQuery({ queryKey: ['recorder-latest'], queryFn: fetchLatestRecorder, staleTime: 60_000 });
  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Create tests faster"
        title="Browser recorder"
        description="Record what you do in the browser and save it as a 1QA test - no typing of steps needed."
        actions={
          <a href={RECORDER_DOWNLOAD} download={recorderFileName(latest.data)} className={buttonClass('primary', 'md')}>
            <Download className="size-3" /> Download recorder
          </a>
        }
      />

      {fromProject && <FromNewTestNotice projectId={fromProject} oldVersion={params.get('update')} />}
      <VersionStatus />

      <Card>
        <CardHeader title="How it works" description="Works in Google Chrome and Microsoft Edge (version 118 or newer)." />
        <CardBody>
          <ol className="grid gap-5">
            {steps.map((s, i) => (
              <li key={s.title} className="flex gap-4">
                <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary-subtle text-primary">
                  <s.icon className="size-3.5" aria-hidden="true" />
                </span>
                <div className="space-y-1 text-sm text-fg-secondary">
                  <h3 className="flex items-baseline gap-2 text-base font-semibold text-fg">
                    <span className="font-display text-sm tracking-label text-primary">{String(i + 1).padStart(2, '0')}</span> {s.title}
                  </h3>
                  {s.body}
                </div>
              </li>
            ))}
          </ol>
        </CardBody>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader title="Passwords become test data" />
          <CardBody className="flex gap-3 text-sm text-fg-secondary">
            <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary-subtle text-primary">
              <KeyRound className="size-3.5" aria-hidden="true" />
            </span>
            <p>
              What you type into password fields is kept out of the step text and saved as <strong>test data</strong> in the project (for example{' '}
              <code className="rounded-bar bg-surface-raised px-1.5 py-0.5 font-mono text-xs text-fg">{'{{PASSWORD}}'}</code>), so you can change it, or use another
              value per collection. It is not secret: the value is visible on the Test data page and in reports. To hide it, mark it{' '}
              <strong>secret</strong> on the Test data page.
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Recording in incognito" />
          <CardBody className="flex gap-3 text-sm text-fg-secondary">
            <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-success-subtle text-success">
              <ShieldCheck className="size-3.5" aria-hidden="true" />
            </span>
            <p>
              Recordings run in incognito by default, and the recorder clears the private session when a recording starts and stops, so a login from
              an earlier recording is never reused. Browsers only allow this after you opt in: open the extensions page, click <strong>Details</strong>{' '}
              on 1QA Recorder and turn on <strong>Allow in Incognito</strong> (until then, recordings use a normal window). Your 1QA sign-in from the
              normal window is still used to save the test.
            </p>
          </CardBody>
        </Card>
        <Card className="md:col-span-2">
          <CardHeader title="Record a performance script" />
          <CardBody className="flex gap-3 text-sm text-fg-secondary">
            <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary-subtle text-primary">
              <Gauge className="size-3.5" aria-hidden="true" />
            </span>
            <p>
              Choose <strong>Performance script</strong> at the top of the recorder (or press <strong>Scripts › New script</strong> in a performance
              project, which starts it for you). It records the requests your app sends (the browser shows a “started
              debugging this browser” bar meanwhile). When you save, 1QA makes a script of them, named after your clicks, in the performance project’s
              Scripts. Recorder {RECORDER_PERF_MIN_VERSION} or newer.
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
